import { useState, useEffect, useRef } from "react";
import { ChevronDown, ChevronLeft, ChevronRight, X, Loader2, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { useRegion } from "../context/RegionContext";
import axios from "axios";
import INDIA_CITIES from "../data/indiaCities";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PORTAL = process.env.REACT_APP_AGENT_PORTAL_URL;

const ALL_DESTINATIONS = ["India", "Sri Lanka", "Maldives", "Bhutan", "Thailand", "Vietnam", "Cambodia", "Laos", "Indonesia", "Bali", "Japan", "South Korea", "Taiwan", "China", "Malaysia", "Singapore", "Philippines", "Kazakhstan", "UAE", "Greece", "Morocco", "South Africa", "Peru", "New Zealand", "Portugal", "Egypt"];

const phoneCodes = [
  { code: "+91", label: "IN +91" }, { code: "+1", label: "US +1" }, { code: "+44", label: "UK +44" },
  { code: "+971", label: "AE +971" }, { code: "+65", label: "SG +65" }, { code: "+61", label: "AU +61" },
  { code: "+49", label: "DE +49" }, { code: "+33", label: "FR +33" }, { code: "+81", label: "JP +81" },
  { code: "+86", label: "CN +86" }, { code: "+55", label: "BR +55" }, { code: "+27", label: "ZA +27" },
  { code: "+966", label: "SA +966" }, { code: "+82", label: "KR +82" }, { code: "+60", label: "MY +60" },
  { code: "+66", label: "TH +66" },
];

const indianStates = Object.keys(INDIA_CITIES).sort();

const banners = [
  { image: "https://images.unsplash.com/photo-1512100356356-de1b84283e18?w=1600&q=85", title: "25+ DMC Destinations", subtitle: "On-ground teams, live rates, and instant confirmations" },
  { image: "https://images.unsplash.com/photo-1506929562872-bb421503ef21?w=1600&q=85", title: "Built for Travel Agents", subtitle: "B2B platform with real-time inventory and best net rates" },
  { image: "https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=1600&q=85", title: "FIT, Groups & MICE", subtitle: "30+ years of ground handling expertise across Asia" },
  { image: "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1600&q=85", title: "850+ Partner Agents", subtitle: "Join the network trusted by agencies worldwide" },
];

const iCls = "h-10 border-[#0D1B2A]/08 text-xs rounded-xl bg-white placeholder:text-[#0D1B2A]/30 focus:border-[#174358]/25";

export default function Hero() {
  const { config } = useRegion();
  const isUsUk = config.code === "us" || config.code === "uk";
  const defaultPhoneCode = config.phoneCode || "+91";

  const [form, setForm] = useState({ agency_name: "", contact_name: "", phone_code: defaultPhoneCode, phone: "", email: "", state: "", city: "", pin_code: "", destinations: [], comments: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [bIdx, setBIdx] = useState(0);
  const [destOpen, setDestOpen] = useState(false);
  const [destQ, setDestQ] = useState("");
  const destRef = useRef(null);

  const DESTINATIONS = config.destinations
    ? ALL_DESTINATIONS.filter(d => config.destinations.includes(d))
    : ALL_DESTINATIONS;

  // Update phone code when region changes
  useEffect(() => {
    setForm(f => ({ ...f, phone_code: defaultPhoneCode }));
  }, [defaultPhoneCode]);

  const isIndia = form.phone_code === "+91";
  const cities = isIndia && form.state ? (INDIA_CITIES[form.state] || []) : [];

  useEffect(() => { const t = setInterval(() => setBIdx((i) => (i + 1) % banners.length), 5000); return () => clearInterval(t); }, []);
  useEffect(() => {
    const handleClick = (e) => { if (destRef.current && !destRef.current.contains(e.target)) setDestOpen(false); };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const u = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const selectState = (v) => setForm((f) => ({ ...f, state: v, city: "", pin_code: "" }));
  const selectCity = (v) => {
    const match = cities.find((c) => c.city === v);
    setForm((f) => ({ ...f, city: v, pin_code: match ? match.pin : "" }));
  };
  const togDest = (d) => setForm((f) => ({ ...f, destinations: f.destinations.includes(d) ? f.destinations.filter((x) => x !== d) : [...f.destinations, d] }));
  const remDest = (d) => setForm((f) => ({ ...f, destinations: f.destinations.filter((x) => x !== d) }));
  const fDests = destQ.trim() ? DESTINATIONS.filter((d) => d.toLowerCase().includes(destQ.toLowerCase())) : DESTINATIONS;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.agency_name.trim()) { toast.error("Agency / Company name is required"); return; }
    if (!form.contact_name.trim()) { toast.error("Contact person name is required"); return; }
    if (!form.phone.trim()) { toast.error("Phone number is required"); return; }
    if (!form.email.trim()) { toast.error("Email is required"); return; }
    if (isIndia && !form.state) { toast.error("Please select your state"); return; }
    if (isIndia && !form.city) { toast.error("Please select your city"); return; }
    if (isIndia && !form.pin_code.trim()) { toast.error("Pin code is required"); return; }
    if (form.destinations.length === 0) { toast.error("Please select at least one destination"); return; }
    setLoading(true);
    try {
      const nameParts = form.contact_name.trim().split(" ");
      const p = {
        first_name: nameParts[0],
        last_name: nameParts.slice(1).join(" ") || "",
        agency_name: form.agency_name,
        email: form.email,
        phone_code: form.phone_code,
        phone: form.phone,
        state: form.state || "",
        source_page: "Homepage Hero",
        form_type: "B2B",
        city: form.city || "",
        pin_code: form.pin_code || "",
        country: isIndia ? "India" : "",
        destination: form.destinations.join(", "),
        comments: "Interested Destinations: " + form.destinations.join(", "),
      };
      await axios.post(`${API}/inquiries`, p);
      setSubmitted(true);
      window.history.pushState({}, "", "/thank-you");
      toast.success("Inquiry submitted!");
    } catch { toast.error("Something went wrong."); }
    finally { setLoading(false); }
  };

  return (
    <section id="hero" data-testid="hero-section" className="relative min-h-screen overflow-hidden bg-[#F5F7FA]">
      <div className="relative z-10 min-h-screen flex items-stretch">
        <div className="flex w-full">

          {/* LEFT — 62% Carousel */}
          <div className="hidden lg:block relative w-[62%]">
            {banners.map((b, i) => (
              <div key={i} className={`absolute inset-0 transition-opacity duration-1000 ${i === bIdx ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
                <img src={b.image} alt={b.title} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0D1B2A]/80 via-[#0D1B2A]/20 to-[#0D1B2A]/05" />
                <div className="absolute bottom-0 left-0 right-0 p-14">
                  {isUsUk ? (
                    <>
                      <p className="text-xs tracking-[0.2em] uppercase font-semibold text-white/40 mb-3">The Leading International DMC</p>
                      <h3 className="font-['Cormorant_Garamond'] text-5xl font-light text-white mb-4 leading-tight">Comprehensive<br />Destination Services</h3>
                      <p className="text-base text-white/60 max-w-md leading-relaxed mb-4">FIT, Group Tours & MICE across 15+ warm water destinations. Run by local experts, trusted by 850+ tour operators worldwide.</p>
                      <div className="flex gap-4 text-white/40 text-xs font-semibold">
                        <span className="border border-white/15 rounded-full px-4 py-1.5">FIT Packages</span>
                        <span className="border border-white/15 rounded-full px-4 py-1.5">Group Tours</span>
                        <span className="border border-white/15 rounded-full px-4 py-1.5">MICE</span>
                        <span className="border border-white/15 rounded-full px-4 py-1.5">Transfers</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <h3 className="font-['Cormorant_Garamond'] text-5xl font-light text-white mb-3">{b.title}</h3>
                      <p className="text-lg text-white/70 max-w-lg">{b.subtitle}</p>
                    </>
                  )}
                </div>
              </div>
            ))}
            <div className="absolute bottom-14 right-14 flex items-center gap-4 z-10">
              <button onClick={() => setBIdx((i) => (i - 1 + banners.length) % banners.length)} className="w-11 h-11 rounded-full bg-white/15 backdrop-blur-md flex items-center justify-center hover:bg-white/30 transition-colors"><ChevronLeft size={20} className="text-white" /></button>
              <div className="flex gap-2">{banners.map((_, i) => (<button key={i} onClick={() => setBIdx(i)} className={`h-2 rounded-full transition-all duration-500 ${i === bIdx ? "w-8 bg-white" : "w-2 bg-white/30"}`} />))}</div>
              <button onClick={() => setBIdx((i) => (i + 1) % banners.length)} className="w-11 h-11 rounded-full bg-white/15 backdrop-blur-md flex items-center justify-center hover:bg-white/30 transition-colors"><ChevronRight size={20} className="text-white" /></button>
            </div>
          </div>

          {/* RIGHT — 38% Form */}
          <div className="w-full lg:w-[38%] flex items-center justify-center px-6 md:px-10 py-24 lg:py-16">
            <div className="w-full max-w-[380px]">
              {submitted ? (
                <div data-testid="hero-success" className="text-center py-8">
                  <div className="w-16 h-16 rounded-2xl bg-emerald-50 flex items-center justify-center mx-auto mb-5">
                    <ArrowRight size={28} className="text-emerald-500" />
                  </div>
                  <h2 className="text-2xl font-bold text-[#0D1B2A] mb-2">Inquiry Received</h2>
                  <p className="text-sm text-[#0D1B2A]/45">Our team will respond with a custom proposal within 24 to 48 hours.</p>
                </div>
              ) : (
                <>
                  <div className="mb-6">
                    <h1 data-testid="hero-heading" className="text-[24px] sm:text-[26px] font-bold text-[#0D1B2A] leading-snug mb-2">
                      Grow Your {isUsUk ? "Travel Company" : "Agency"}<br />with <span className="text-[#174358]">Hi DMC</span>
                    </h1>
                    <p className="text-[13px] text-[#0D1B2A]/45 leading-relaxed">
                      {isUsUk
                        ? "Comprehensive destination services across warm water destinations, run by local experts."
                        : "Best net rates, live inventory, and on-ground DMC support across 25+ destinations."}
                    </p>
                  </div>

                  <form onSubmit={submit} className="space-y-2">
                    <Input data-testid="hero-agency" value={form.agency_name} onChange={(e) => u("agency_name", e.target.value)} placeholder="Agency / Company Full Name *" className={iCls} />
                    <Input data-testid="hero-name" value={form.contact_name} onChange={(e) => u("contact_name", e.target.value)} placeholder="Contact Person Name *" className={iCls} />

                    <div className="flex gap-2">
                      <Select value={form.phone_code} onValueChange={(v) => { u("phone_code", v); if (v !== "+91") { u("state", ""); u("city", ""); u("pin_code", ""); } }}>
                        <SelectTrigger data-testid="hero-phone-code" className="w-[90px] h-10 border-[#0D1B2A]/08 rounded-xl text-[10px] bg-white flex-shrink-0"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white max-h-56">{phoneCodes.map((p) => <SelectItem key={p.code} value={p.code} className="text-xs">{p.label}</SelectItem>)}</SelectContent>
                      </Select>
                      <Input data-testid="hero-phone" type="tel" value={form.phone} onChange={(e) => u("phone", e.target.value)} placeholder="Phone Number *" className={`flex-1 ${iCls}`} />
                    </div>

                    <Input data-testid="hero-email" type="email" value={form.email} onChange={(e) => u("email", e.target.value)} placeholder="Email *" className={iCls} />

                    {isIndia && !isUsUk && (
                      <>
                        <Select value={form.state} onValueChange={selectState}>
                          <SelectTrigger data-testid="hero-state" className="h-10 border-[#0D1B2A]/08 rounded-xl text-xs bg-white"><SelectValue placeholder="State *" /></SelectTrigger>
                          <SelectContent className="bg-white max-h-56">{indianStates.map((s) => <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>)}</SelectContent>
                        </Select>

                        {form.state && (
                          <div className="grid grid-cols-2 gap-2">
                            <Select value={form.city} onValueChange={selectCity}>
                              <SelectTrigger data-testid="hero-city" className="h-10 border-[#0D1B2A]/08 rounded-xl text-xs bg-white"><SelectValue placeholder="City / District *" /></SelectTrigger>
                              <SelectContent className="bg-white max-h-56">{cities.map((c) => <SelectItem key={c.city} value={c.city} className="text-xs">{c.city}</SelectItem>)}</SelectContent>
                            </Select>
                            <Input data-testid="hero-pincode" value={form.pin_code} onChange={(e) => u("pin_code", e.target.value)} placeholder="Pin Code" className={iCls} />
                          </div>
                        )}
                      </>
                    )}

                    {/* Destinations — inline search + tag */}
                    <div className="relative" data-testid="hero-destinations-wrap" ref={destRef}>
                      <div className="flex flex-wrap items-center gap-1 px-2.5 py-1.5 rounded-xl border border-[#0D1B2A]/08 bg-white min-h-[40px] focus-within:border-[#174358]/25 transition-colors">
                        {form.destinations.map((d) => (
                          <span key={d} className="inline-flex items-center gap-0.5 bg-[#174358] text-white text-[10px] font-semibold px-2 py-0.5 rounded-full">
                            {d} <X size={9} className="cursor-pointer opacity-70 hover:opacity-100" onClick={() => remDest(d)} />
                          </span>
                        ))}
                        <input
                          data-testid="hero-dest-search"
                          type="text"
                          placeholder={form.destinations.length ? "Add more..." : "Search destinations *"}
                          value={destQ}
                          onChange={(e) => { setDestQ(e.target.value); setDestOpen(true); }}
                          onFocus={() => setDestOpen(true)}
                          className="flex-1 min-w-[100px] text-xs text-[#0D1B2A] placeholder:text-[#0D1B2A]/30 focus:outline-none bg-transparent py-0.5"
                        />
                      </div>
                      {destOpen && fDests.length > 0 && (
                        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-[#0D1B2A]/08 rounded-xl shadow-2xl max-h-40 overflow-y-auto p-1">
                          {fDests.filter(d => !form.destinations.includes(d)).map((d) => (
                            <button key={d} type="button" onClick={() => { togDest(d); setDestQ(""); }}
                              className="w-full text-left px-2.5 py-1.5 text-xs rounded-lg text-[#0D1B2A]/55 hover:bg-[#174358]/06 hover:text-[#174358] transition-colors">{d}</button>
                          ))}
                        </div>
                      )}
                    </div>

                    <Button data-testid="hero-inquire" type="submit" disabled={loading}
                      className="w-full bg-[#174358] text-white hover:bg-[#1e5a75] rounded-xl py-4 text-xs font-bold shadow-md shadow-[#174358]/10 disabled:opacity-60">
                      {loading ? <><Loader2 size={14} className="mr-2 animate-spin" /> Submitting...</> : "Inquire"}
                    </Button>

                  </form>
                </>
              )}
            </div>
          </div>

        </div>
      </div>

      {/* Scroll indicator */}
      <a href="#stats" data-testid="scroll-indicator" className="absolute bottom-5 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 text-[#0D1B2A]/20 hover:text-[#0D1B2A]/50 transition-colors animate-float z-20">
        <span className="text-[9px] tracking-[0.2em] uppercase font-semibold">Scroll</span><ChevronDown size={14} />
      </a>
    </section>
  );
}
